import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useMyReceivedFeedbackApi, CategoryBadge, useFeedbackRequestsApi, RequestStatusBadge,
} from './config';
import RequestFeedbackModal from './components/RequestFeedbackModal';
import { useAuth } from '../../context/AuthContext';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { formatDate } from '../../utils/Utils';

// Task 31 — Sürekli Geri Bildirim: "aldıklarım" self-servis kart listesi.
// Faz D (spec 2026-08-08 §4, Task 5, D3) — "Taleplerim" sekmesi eklendi:
// gönderdiklerim (durum rozeti + pending'de İptal) + bana gelenler (pending'de
// Yanıtla → /feedback/send?requestId=X + Reddet) + "Geri Bildirim İste" modalı.
// GetMyReceivedAsync DÜZ bir liste döner (PaginatedList DEĞİL) — bu yüzden
// DataTable yerine (Task 26/30'un self-servis küçük liste emsali gibi) basit
// bir kart listesi kullanılıyor. Gönderen alanı ReceivedFeedbackDto'da TİP
// DÜZEYİNDE yok — anonimse SenderName her zaman null (backend hiçbir zaman
// şifreli kimliği çözmez), FE burada yalnız SenderName varsa gösterir,
// yoksa "Anonim" yazar.
const Container = styled.div``;

const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 12px;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const SenderText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DateText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const FeedbackText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
  margin-bottom: 8px;
`;

const EmptyBox = styled.div`
  padding: 40px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
`;

const RequestsHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 10px;
`;

const RowActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

export default function MyFeedbackPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Fix round 1 (coordinator ruling) — `?tab=requests` ile açılınca Taleplerim
  // sekmesi aktif başlar. BenefitReportsPage.js'teki AYNI desen (initialTab
  // useMemo'dan location.search okur, gerçek kontrol useState'te — sekmeler
  // arası tıklama sonrasında URL'i TAKİP ETMEZ, yalnız İLK render'ı belirler).
  // Not (2026-10-28): Gelen Kutum'un `feedback_request` kartı artık buraya
  // değil, doğrudan talebi yanıtlayan `/feedback/send?requestId=` ekranına
  // gider (BE kayda özgü bağlantı üretir); bu sekme parametresi diğer
  // girişler için korunur.
  const initialTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    return qs.get('tab') === 'requests' ? 'requests' : 'feedback';
  }, [location.search]);

  const [activeTab, setActiveTab] = useState(initialTab);

  const { data, fetching } = useMyReceivedFeedbackApi();
  const {
    sent, received, fetching: requestsFetching, createRequest, cancelRequest, declineRequest,
  } = useFeedbackRequestsApi();

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [declineTargetId, setDeclineTargetId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const currentEmployeeId = user?.employeeId;

  const goRespond = useCallback((requestId) => {
    history.push(`/feedback/send?requestId=${requestId}`);
  }, [history]);

  const handleConfirmCancel = useCallback(async () => {
    setConfirmBusy(true);
    await cancelRequest(cancelTargetId);
    setConfirmBusy(false);
    setCancelTargetId(null);
  }, [cancelRequest, cancelTargetId]);

  const handleConfirmDecline = useCallback(async () => {
    setConfirmBusy(true);
    await declineRequest(declineTargetId);
    setConfirmBusy(false);
    setDeclineTargetId(null);
  }, [declineRequest, declineTargetId]);

  const tabs = [
    { key: 'feedback', label: t('feedbackRequests.tab.feedback') },
    { key: 'requests', label: t('feedbackRequests.tab.requests') },
  ];

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.feedback.myTitle')}</PageTitle>
      </PageHeader>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'feedback' && (
        <>
          {!fetching && data.length === 0 && (
            <EmptyBox>{t('performance.feedback.noFeedback')}</EmptyBox>
          )}

          {data.map((item) => (
            <Card key={item.id}>
              <CardTopRow>
                <CategoryBadge $category={item.category}>
                  {t(`performance.feedback.category.${item.category}`, { defaultValue: item.category })}
                </CategoryBadge>
                <DateText>{formatDate(item.createDate, i18n.language)}</DateText>
              </CardTopRow>
              <SenderText>
                {t('performance.feedback.sender')}: {item.isAnonymous ? t('performance.feedback.anonymous') : (item.senderName || '-')}
              </SenderText>
              {item.text && <FeedbackText>{item.text}</FeedbackText>}
            </Card>
          ))}
        </>
      )}

      {activeTab === 'requests' && (
        <>
          <RequestsHeader>
            <Button data-role="new-request-btn" onClick={() => setShowRequestModal(true)}>
              {t('feedbackRequests.newRequest')}
            </Button>
          </RequestsHeader>

          <SectionTitle>{t('feedbackRequests.sentTitle')}</SectionTitle>
          {!requestsFetching && sent.length === 0 && (
            <EmptyBox>{t('feedbackRequests.noSent')}</EmptyBox>
          )}
          {sent.map((r) => (
            <Card key={r.id} data-role={`sent-request-${r.id}`}>
              <CardTopRow>
                <SenderText>{r.targetName}</SenderText>
                <RequestStatusBadge $status={r.status}>
                  {t(`feedbackRequests.status.${r.status}`, { defaultValue: r.status })}
                </RequestStatusBadge>
              </CardTopRow>
              {r.message && <FeedbackText>{r.message}</FeedbackText>}
              <DateText>{formatDate(r.createDate, i18n.language)}</DateText>
              {r.status === 'pending' && (
                <RowActions>
                  <Button variant="outline" onClick={() => setCancelTargetId(r.id)}>
                    {t('feedbackRequests.actions.cancel')}
                  </Button>
                </RowActions>
              )}
            </Card>
          ))}

          <SectionTitle>{t('feedbackRequests.receivedTitle')}</SectionTitle>
          {!requestsFetching && received.length === 0 && (
            <EmptyBox>{t('feedbackRequests.noReceived')}</EmptyBox>
          )}
          {received.map((r) => (
            <Card key={r.id} data-role={`received-request-${r.id}`}>
              <CardTopRow>
                <SenderText>{r.requesterName}</SenderText>
                <RequestStatusBadge $status={r.status}>
                  {t(`feedbackRequests.status.${r.status}`, { defaultValue: r.status })}
                </RequestStatusBadge>
              </CardTopRow>
              {r.message && <FeedbackText>{r.message}</FeedbackText>}
              <DateText>{formatDate(r.createDate, i18n.language)}</DateText>
              {r.status === 'pending' && (
                <RowActions>
                  <Button onClick={() => goRespond(r.id)}>
                    {t('feedbackRequests.actions.respond')}
                  </Button>
                  <Button variant="outline" onClick={() => setDeclineTargetId(r.id)}>
                    {t('feedbackRequests.actions.decline')}
                  </Button>
                </RowActions>
              )}
            </Card>
          ))}
        </>
      )}

      {showRequestModal && (
        <RequestFeedbackModal
          excludeEmployeeId={currentEmployeeId}
          onClose={() => setShowRequestModal(false)}
          onCreate={createRequest}
        />
      )}

      <ConfirmDialog
        isOpen={!!cancelTargetId}
        title={t('feedbackRequests.cancelConfirm.title')}
        message={t('feedbackRequests.cancelConfirm.message')}
        onCancel={() => setCancelTargetId(null)}
        onConfirm={handleConfirmCancel}
        loading={confirmBusy}
      />
      <ConfirmDialog
        isOpen={!!declineTargetId}
        title={t('feedbackRequests.declineConfirm.title')}
        message={t('feedbackRequests.declineConfirm.message')}
        onCancel={() => setDeclineTargetId(null)}
        onConfirm={handleConfirmDecline}
        loading={confirmBusy}
      />
    </Container>
  );
}
